import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";
import { assetTypeValues } from "../../../constants";

export const GEO_JURISDICTION_BREAKDOWN_COLUMNS = [
  { key: "lease_ref_code", label: "Lease Ref Code" },
  { key: "lessee", label: "Lessee" },
  { key: "asset_label", label: "Asset" },
  { key: "revenue_exposure", label: "Revenue Exposure", mono: true },
];

function formatAmount(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (Number.isNaN(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}

function buildJurisdictionRows(items, currency) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => ({
    id: `${item?.lease_ref_code || "lease"}-${index}`,
    jurisdiction: item?.jurisdiction || "Unknown",
    lease_ref_code: item?.lease_ref_code || "-",
    lessee: item?.lessee || "-",
    asset_label: [assetTypeValues[item?.asset_type]?.label, item?.asset_sn].filter(Boolean).join(" ") || "-",
    revenue_exposure: formatAmount(item?.revenue_exposure, currency),
  }));
}

// Clicking a bar opens the popup with geo_jurisdiction_con_breakdown filtered to that jurisdiction.
export function useGeoJurisdictionConcentrationBreakdown({ jurisdictionBreakdown, currency, onTrack }) {
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);
  const reset = useCallback(() => setSelectedJurisdiction(null), []);

  const allRows = useMemo(() => buildJurisdictionRows(jurisdictionBreakdown, currency), [jurisdictionBreakdown, currency]);
  const rows = useMemo(
    () => (selectedJurisdiction ? allRows.filter((row) => row.jurisdiction === selectedJurisdiction) : []),
    [allRows, selectedJurisdiction],
  );

  const chartEvents = useMemo(() => ({
    click: (params) => {
      if (params?.componentType !== "series") return;
      setSelectedJurisdiction(params.name);
      onTrack?.(`Graph Bar Clicked: ${params.name}`);
    },
  }), [onTrack]);

  return { rows, canRender: allRows.length > 0, chartEvents, modalOpen: !!selectedJurisdiction, closeModal: reset, titleSuffix: selectedJurisdiction || "", reset };
}

export function GeoJurisdictionConcentrationBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Geographic & Jurisdiction Concentration";
  const rowCount = Array.isArray(rows) ? rows.length : 0;
  const countLabel = `${rowCount} ${rowCount === 1 ? "Lease" : "Leases"}`;
  const subtitle = titleSuffix ? `${titleSuffix}  (${countLabel})` : countLabel;
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      exportFileName={titleSuffix ? `${title} - ${titleSuffix}` : title}
      columns={GEO_JURISDICTION_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
